// W42 W1 (ADR-0107) — the screen-type context provider.
//
// Split out of `lib/screenType.ts` for one reason: `eslint --max-warnings=0`
// runs `react-refresh/only-export-components`, and a module that exports both a
// component and plain data falls back to a full page reload on every edit. The
// data, the route table and the hook stay in `lib/screenType.ts`; this file
// exports components only. Same split as `mascots.ts` / `Mascot.tsx`.

import { useMemo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { ScreenTypeContext, screenTypeForPath } from "@/lib/screenType";

export interface ScreenTypeProviderProps {
  children: ReactNode;
}

/**
 * Resolves the screen type from the URL and publishes it.
 *
 * Mounted once, at the AppShell root, because that is the only common ancestor
 * of all 39 production routes. Set per page it would be opt-in again — the exact
 * failure W42 exists to fix, where the saturated tokens were reachable only by a
 * component asking for one by hand and three did.
 *
 * `useMemo` on the pathname is not a micro-optimisation: the context value is
 * read by every `Card` in the tree, and a fresh object identity on each parent
 * render would re-render all of them.
 */
export function ScreenTypeProvider({ children }: ScreenTypeProviderProps): JSX.Element {
  const { pathname } = useLocation();
  const value = useMemo(() => screenTypeForPath(pathname), [pathname]);
  return <ScreenTypeContext.Provider value={value}>{children}</ScreenTypeContext.Provider>;
}
